import React, {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import {ErrorType} from 'utils/types';
import {DEVICE, TooltipInfo, useScreenResize} from '@chekin/guestapp-ui-kit';
import {ErrorMessage, Fieldset} from '@guestapp/old-ui-kit';
import {RelativeWrapper, StyledInput, WrapperInput} from './styled';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  invalid?: boolean;
  error?: ErrorType;
  inputMode?:
    | 'text'
    | 'none'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  tooltipContent?: string;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      onChange,
      className,
      label,
      defaultValue,
      disabled,
      name,
      type,
      max,
      error,
      autoComplete = 'on',
      inputMode,
      children,
      tooltipContent,
      onBlur,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const {isMatch: isMobileMode} = useScreenResize(DEVICE.laptop);
    const [currentValue, setCurrentValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleLabelClick = useCallback(() => {
      inputRef.current?.focus();
      setIsFocused(true);
    }, [setIsFocused]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setCurrentValue(event.target.value);
        onChange?.(event);
      },
      [setCurrentValue, onChange],
    );

    const handleFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        onFocus?.(event);
        setIsFocused(true);
      },
      [setIsFocused, onFocus],
    );

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onBlur?.(event);
        setIsFocused(false);
      },
      [onBlur, setIsFocused],
    );
    const [isEmpty, setIsEmpty] = useState(true);

    useEffect(() => {
      const formValue = inputRef.current?.value;
      setIsEmpty(!currentValue && !formValue);
    }, [currentValue, inputRef.current?.value]);

    // const isEmpty = React.useMemo(() => {
    //   const formValue = inputRef.current?.value;
    //   return !Boolean(currentValue) && !Boolean(formValue);
    // }, [currentValue]);

    return (
      <WrapperInput disabled={disabled} className={clsx(className, 'input-wrapper')}>
        <RelativeWrapper className="input">
          {children || (
            <StyledInput
              className="input__input"
              ref={inputRef}
              type={type}
              data-testid="input"
              value={value}
              $isFocused={isFocused}
              autoFocus={isFocused}
              onChange={handleChange}
              disabled={disabled}
              defaultValue={defaultValue}
              aria-label={name}
              name={name}
              max={max}
              $invalid={!!error}
              autoComplete={autoComplete}
              inputMode={inputMode}
              $isEmpty={isEmpty}
              placeholder={''}
              onFocus={handleFocus}
              onBlur={handleBlur}
              {...props}
            />
          )}
          {tooltipContent && (
            <TooltipInfo
              className="absolute -right-6 top-1/2 -translate-y-1/2"
              side={isMobileMode ? 'top' : 'right'}
              content={tooltipContent}
            />
          )}
          <Fieldset
            invalid={!!error}
            isFocused={isFocused}
            isEmpty={isEmpty}
            isActivated={!isEmpty || isFocused}
            onClick={handleLabelClick}
            htmlFor={props.placeholder}
            legend={label}
            label={label}
          />
        </RelativeWrapper>

        {error && <ErrorMessage>{error as string}</ErrorMessage>}
      </WrapperInput>
    );
  },
);

export {Input};
